import { ProviderType } from '@/reporting';
import type { TrackEventFunction } from 'index';

/** 第三方登录回跳中必须从埋点与地址栏删除的认证参数。 */
export const THIRD_PARTY_AUTH_QUERY_PARAMS = [
  'token',
  'store_id',
  'isRegister',
  'registerType',
  'eventType',
  'third_login_msg',
] as const;

/** 第三方登录清理后的埋点 URL 与站内导航路径。 */
export interface ThirdPartyLoginUrlInfo {
  trackingUrl: string;
  navigationPath: string;
}

/** 第三方登录成功结果发送给神策的完整属性。 */
export interface ThirdPartyLoginSuccessProperties {
  is_new: boolean;
  url_path: string;
  login_method: string;
  account_type: '三方账号';
  account_platform: string;
  login_type: string | undefined;
  group_id: unknown;
  product_sku: unknown;
  shop_sku: unknown;
  page_name: string;
  login_situation_new: string;
}

/** 第三方登录各阶段的稳定日志标识。 */
export type ThirdPartyLoginStage =
  | 'prepareToken'
  | 'fetchUser'
  | 'handleLoginSuccess'
  | 'updateCompliance'
  | 'trackAppLovin'
  | 'trackSnapchat'
  | 'trackLoginSuccess'
  | 'clearUrl';

/** 非 App 第三方登录编排所需的可注入依赖。 */
export interface RunThirdPartyLoginFlowOptions<TUser> {
  urlInfo: ThirdPartyLoginUrlInfo;
  prepareToken: () => void | Promise<void>;
  fetchUser: () => Promise<TUser | null>;
  handleLoginSuccess: (user: TUser) => Promise<void>;
  updateCompliance?: (user: TUser) => Promise<void>;
  trackAppLovin: (user: TUser) => void;
  trackSnapchat?: (user: TUser) => void;
  trackLoginSuccess?: (user: TUser, trackingUrl: string) => void;
  clearUrl: (navigationPath: string) => void | Promise<void>;
  onError: (stage: ThirdPartyLoginStage, error: unknown) => void;
}

/** 删除第三方认证参数，同时生成埋点完整 URL 和路由相对路径。 */
export function sanitizeThirdPartyLoginUrl(href: string): ThirdPartyLoginUrlInfo {
  const url = new URL(href);
  THIRD_PARTY_AUTH_QUERY_PARAMS.forEach((param) => url.searchParams.delete(param));
  return {
    trackingUrl: url.toString(),
    navigationPath: `${url.pathname}${url.search}${url.hash}`,
  };
}

/** 运行一个可降级阶段，并把异常交给统一日志回调。 */
async function runThirdPartyStage(
  stage: ThirdPartyLoginStage,
  task: () => void | Promise<void>,
  onError: RunThirdPartyLoginFlowOptions<unknown>['onError'],
): Promise<void> {
  try {
    await task();
  } catch (error) {
    onError(stage, error);
  }
}

/** 运行非 App 第三方登录流程，保证阶段互不阻断且 URL 最终清理。 */
export async function runThirdPartyLoginFlow<TUser>(
  options: RunThirdPartyLoginFlowOptions<TUser>,
): Promise<void> {
  const {
    urlInfo,
    prepareToken,
    fetchUser,
    handleLoginSuccess,
    updateCompliance,
    trackAppLovin,
    trackSnapchat,
    trackLoginSuccess,
    clearUrl,
    onError,
  } = options;

  try {
    await runThirdPartyStage('prepareToken', prepareToken, onError);

    let user: TUser | null = null;
    try {
      user = await fetchUser();
    } catch (error) {
      onError('fetchUser', error);
      return;
    }
    if (!user) return;
    const authenticatedUser = user;

    await runThirdPartyStage(
      'handleLoginSuccess',
      () => handleLoginSuccess(authenticatedUser),
      onError,
    );
    if (updateCompliance) {
      await runThirdPartyStage(
        'updateCompliance',
        () => updateCompliance(authenticatedUser),
        onError,
      );
    }
    await runThirdPartyStage('trackAppLovin', () => trackAppLovin(authenticatedUser), onError);
    if (trackSnapchat) {
      await runThirdPartyStage('trackSnapchat', () => trackSnapchat(authenticatedUser), onError);
    }
    if (trackLoginSuccess) {
      await runThirdPartyStage(
        'trackLoginSuccess',
        () => trackLoginSuccess(authenticatedUser, urlInfo.trackingUrl),
        onError,
      );
    }
  } finally {
    await runThirdPartyStage('clearUrl', () => clearUrl(urlInfo.navigationPath), onError);
  }
}

/** 将第三方登录成功结果及其完整属性只发送给神策 Provider。 */
export function reportThirdPartyLoginSuccess(
  trackEvent: TrackEventFunction,
  properties: ThirdPartyLoginSuccessProperties,
): void {
  trackEvent(
    {
      eventName: 'LoginSuccess_Result',
      properties: {
        ...properties,
        // 覆盖 SensorsProvider 默认读取的原始地址，避免第三方回跳认证参数进入神策 $url。
        $url: properties.url_path,
      },
    },
    [ProviderType.SENSORS],
  );
}
